"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface BFLCarouselProps {
  children: React.ReactNode[] | React.ReactNode;
  slidesPerView?: number;
  autoplay?: boolean;
  interval?: number;
  showNavigation?: boolean;
  showDots?: boolean;
  loop?: boolean;
  className?: string;
  maintainSlidesOnMobile?: boolean;
  minSlideWidth?: number;
  scrollar?: boolean;
  gap?: number;
}

const BFLCarouselNew: React.FC<BFLCarouselProps> = ({
  children,
  slidesPerView = 1,
  autoplay = false,
  interval = 3000,
  showNavigation = true,
  showDots = true,
  loop = false,
  className = "",
  maintainSlidesOnMobile = false,
  minSlideWidth = 351,
  scrollar,
  gap = 8,
}) => {
  const childrenArray = Array.isArray(children) ? children : [children];
  const totalSlides = childrenArray.length;

  const [slideWidthPx, setSlideWidthPx] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [containerW, setContainerW] = useState(0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startX = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startScrollLeft = useRef(0);

  const trackW = useMemo(
    () => totalSlides * slideWidthPx + (totalSlides - 1) * gap,
    [totalSlides, slideWidthPx, gap]
  );
  const maxTranslate = useMemo(() => Math.max(0, trackW - containerW), [trackW, containerW]);

  const clampTranslate = (index: number) => {
    const desired = index * (slideWidthPx + gap);
    return Math.min(desired, maxTranslate);
  };

  useEffect(() => {
    const handleResize = () => {
      const vw = window.innerWidth;
      setIsMobile(vw <= 1199);

      const measuredContainer = scrollRef.current?.offsetWidth ?? vw;
      const totalGapSpace = (slidesPerView - 1) * gap;
      const availableWidth = measuredContainer - totalGapSpace;
      const desiredWidth = availableWidth / slidesPerView;
      const newSlideWidthPx = Math.max(desiredWidth, minSlideWidth);

      const newTrackW = totalSlides * newSlideWidthPx + (totalSlides - 1) * gap;
      const newMaxTranslate = Math.max(0, newTrackW - measuredContainer);
      const newMaxIndex =
        newSlideWidthPx > 0 ? Math.max(0, Math.ceil(newMaxTranslate / (newSlideWidthPx + gap))) : 0;

      setContainerW(measuredContainer);
      setSlideWidthPx(newSlideWidthPx);
      setMaxIndex(newMaxIndex);
      setCurrentIndex((prev) => Math.min(prev, newMaxIndex));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [slidesPerView, totalSlides, minSlideWidth, maintainSlidesOnMobile, gap]);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (touch) startX.current = touch.clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (startX.current === null) return;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const diff = startX.current - touch.clientX;
    const threshold = 50;
    if (diff > threshold) {
      nextSlide();
    } else if (diff < -threshold) {
      prevSlide();
    }
    startX.current = null;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMobile || !scrollRef.current) return;
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    startScrollLeft.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = "grabbing";
    scrollRef.current.style.userSelect = "none";
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - (startX.current ?? x)) * 2;
    scrollRef.current.scrollLeft = startScrollLeft.current - walk;
  };

  const handleMouseUp = () => {
    if (!scrollRef.current) return;
    isDragging.current = false;
    scrollRef.current.style.cursor = "grab";
    scrollRef.current.style.removeProperty("user-select");
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      handleMouseUp();
    }
  };

  const handleScroll = () => {
    if (!isMobile || !scrollRef.current || slideWidthPx === 0) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const newIndex = Math.round(scrollLeft / (slideWidthPx + gap));
    setCurrentIndex(Math.min(newIndex, maxIndex));
  };

  const getContainerClassName = () => {
    if (isMobile) {
      return "snap-x snap-mandatory overflow-x-auto";
    }
    return scrollar ? "overflow-x-auto" : "overflow-hidden";
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        prevSlide();
        break;
      case "ArrowRight":
        e.preventDefault();
        nextSlide();
        break;
      case "Home":
        e.preventDefault();
        setCurrentIndex(0);
        if (scrollRef.current && slideWidthPx > 0) {
          scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
        }
        break;
      case "End":
        e.preventDefault();
        setCurrentIndex(maxIndex);
        if (scrollRef.current && slideWidthPx > 0) {
          const left = Math.min(maxIndex * (slideWidthPx + gap), maxTranslate);
          scrollRef.current.scrollTo({ left, behavior: "smooth" });
        }
        break;
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      let newIndex = prev + 1;
      if (newIndex > maxIndex) {
        newIndex = loop ? 0 : prev;
      }
      if (isMobile && scrollRef.current && slideWidthPx > 0) {
        const left = Math.min(newIndex * (slideWidthPx + gap), maxTranslate);
        scrollRef.current.scrollTo({ left, behavior: "smooth" });
      }
      return newIndex;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      let newIndex = prev - 1;
      if (newIndex < 0) {
        newIndex = loop ? maxIndex : 0;
      }
      if (isMobile && scrollRef.current && slideWidthPx > 0) {
        const left = Math.min(newIndex * (slideWidthPx + gap), maxTranslate);
        scrollRef.current.scrollTo({ left, behavior: "smooth" });
      }
      return newIndex;
    });
  };

  useEffect(() => {
    if (!autoplay) return;
    if (trackW <= containerW) return;

    timeoutRef.current = setTimeout(() => {
      nextSlide();
    }, interval);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [currentIndex, autoplay, interval, trackW, containerW]);

  if (totalSlides <= 1) {
    return (
      <div className={`relative w-full ${className}`}>
        <div className="flex w-full justify-start">
          <div
            className="shrink-0"
            style={{
              width: minSlideWidth ? `${minSlideWidth}px` : undefined,
              maxWidth: "100%",
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }

  const containerClassName = getContainerClassName();

  return (
    <div className={`relative w-full ${className}`}>
      <section
        ref={scrollRef}
        className={containerClassName}
        aria-label="Carousel"
        style={{
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          cursor: isMobile ? "grab" : "default",
        }}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
      >
        <div
          className={`flex ${!isMobile ? "transition-transform duration-500 ease-in-out" : ""}`}
          style={{
            transform:
              !isMobile && slideWidthPx > 0
                ? `translateX(-${clampTranslate(currentIndex)}px)`
                : undefined,
            gap: gap > 0 ? `${gap}px` : undefined,
          }}
          onTouchStart={!isMobile ? handleTouchStart : undefined}
          onTouchEnd={!isMobile ? handleTouchEnd : undefined}
        >
          {childrenArray.map((child, index) => (
            <div
              key={`carousel-${index}`}
              className={`flex-shrink-0 ${isMobile ? "snap-center" : ""}`}
              style={{
                width: slideWidthPx > 0 ? `${slideWidthPx}px` : "100%",
                padding: 0,
                margin: 0,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </section>

      {showNavigation && !isMobile && trackW > containerW && (
        <>
          {(currentIndex > 0 || loop) && (
            <button
              onClick={prevSlide}
              className="text-link-primary absolute top-1/2 left-0 z-2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-r-md bg-white shadow-lg transition-opacity"
              aria-label="Previous slide"
            >
              <i className="icomoon text-sm before:content-['\e912']"></i>
            </button>
          )}
          {(currentIndex < maxIndex || loop) && (
            <button
              onClick={nextSlide}
              className="text-link-primary absolute top-1/2 right-0 z-2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-l-md bg-white shadow-lg transition-opacity"
              aria-label="Next slide"
            >
              <i className="icomoon text-sm before:content-['\e911']"></i>
            </button>
          )}
        </>
      )}

      {showDots && trackW > containerW && (
        <div className="mt-4 flex justify-center space-x-2">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={`dot-${index}`}
              onClick={() => {
                setCurrentIndex(index);
                if (isMobile && scrollRef.current && slideWidthPx > 0) {
                  const left = Math.min(index * (slideWidthPx + gap), maxTranslate);
                  scrollRef.current.scrollTo({ left, behavior: "smooth" });
                }
              }}
              className={`h-2.5 w-2.5 rounded-full transition-colors ${
                currentIndex === index ? "bg-gray-800" : "bg-gray-300"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BFLCarouselNew;
